import { useContext, useEffect } from 'react'
import swal from 'sweetalert2'
import { DateTime } from 'luxon'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faTimes } from '@fortawesome/pro-solid-svg-icons'

import {
  Button, Form, ModalContext, SectionDivider, SidebarModal, SidebarModalListItem,
} from '@domain-group/suit-theme'

import { useSetState } from '@domain-group/react-hooks'

import { toggleArray } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { EntityOptions } from '@redux/modules/types'
import type { TaskListModel } from '@models/taskList'

import useTaskList, { useTaskListForm } from '@hooks/useTaskList'
import type { UseTaskListPayload } from '@hooks/useTaskList'

import EntitySelector from '@components/EntitySelector'
import ToggleListItem from '@components/ToggleListItem'
import OrganizationSelector from '@components/OrganizationSelector'

const MODAL_KEY = 'CreateOrEditTaskListModal'

type ModalCallbacks = {
  closeModal: () => void,
  createTaskList: (payload: HandleCallbackActionParams) => void,
  createFromUsageReport: (payload: HandleCallbackActionParams) => void,
  toggleTaskListCompletion?: (payload: HandleCallbackActionParams) => void,
  updateTaskList: (payload: HandleCallbackActionParams) => void,
}

type ConfirmCompletionParams = {
  callbacks: ModalCallbacks,
  entityState: Partial<TaskListModel>,
  toggleCompletionFn: UseTaskListPayload['callbacks']['toggleCompletion'],
}

const confirmCompletion = (params: ConfirmCompletionParams) => {
  const {
    callbacks, entityState, entityState: { completed }, toggleCompletionFn,
  } = params
  const { closeModal, toggleTaskListCompletion } = callbacks

  const toggleTaskListCompletionPayload = {
    callbacks: {
      action: toggleCompletionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Task List Updated',
  }

  swal
    .fire({
      confirmButtonColor: '#43ac6a',
      confirmButtonText: completed ? 'Yes, mark as incomplete.' : 'Yes, complete it.',
      icon: 'success',
      showCancelButton: true,
      text: 'Are you sure?',
      title: 'Toggle Completion?',
    })
    .then(({ value }) => {
      if (value){
        // @ts-expect-error TS2722
        toggleTaskListCompletion(toggleTaskListCompletionPayload)
      }
    })
}

const defaultState = {
  createUsingUsageReport: false,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  requestOptions?: EntityOptions,
  showModal: boolean,
}

const CreateOrEditTaskListModal = (props: ModalProps) => {
  const { callbacks, requestOptions, showModal } = props
  const {
    closeModal, createTaskList, createFromUsageReport, toggleTaskListCompletion, updateTaskList,
  } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] ?? {}
  const taskList = modalPayload?.taskList ?? {}
  const showCreateFromUsageReport = modalPayload?.createUsingUsageReport ?? false

  const [state, setState] = useSetState(defaultState)
  const { createUsingUsageReport } = state

  useEffect(() => {
    setState({ createUsingUsageReport: showCreateFromUsageReport })
  }, [])

  const newRecord = !taskList.id

  const {
    callbacks: {
      createTaskList: createFn,
      createFromUsageReport: createFromUsageReportFn,
      toggleCompletion: toggleCompletionFn,
      updateTaskList: updateFn,
    },
    creating,
    updating,
  } = useTaskList(taskList)

  const {
    entityState,
    entityState: {
      ad_hoc,
      checklist_template_id,
      completed,
      debtor_id,
      debtor_type,
      due_date,
      end_date,
      notification_emails,
      notification_frequency,
      owner_id,
      owner_type,
      start_date,
      subject_id,
      subject_type,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useTaskListForm(taskList, { validateOn: 'change' })

  // To be used with handleCallbackAction
  const actionFn = () => {
    if (!newRecord){
      return updateFn
    }

    return createUsingUsageReport ? createFromUsageReportFn : createFn
  }
  const createOrUpdateTaskListPayload = {
    callbacks: {
      action: actionFn(),
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions,
    toastText: `Project ${newRecord ? 'Created' : 'Updated'}`,
  }

  const createTaskListFn = createUsingUsageReport ? createFromUsageReport : createTaskList

  const createButtonText = createUsingUsageReport ? 'Create Project From Usage Report' : 'Create Project'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Create' : 'Edit'} titleSecondLine="Project" />

      <SidebarModal.Content>
        <Form>
          {owner_type === 'Brand' && (
            <EntitySelector
              // @ts-expect-error TS2741
              callbacks={{
                selectItem: brand => setEntityState({
                  debtor_id: brand?.id,
                  debtor_type: 'Brand',
                  due_date: ad_hoc ? DateTime.now().startOf('month').toFormat('yyyy-MM-dd') : due_date,
                  owner_id: brand?.id,
                  subject_id: ad_hoc && brand ? brand.id : subject_id,
                  subject_type: ad_hoc ? 'Brand' : subject_type,
                  title: ad_hoc ? `Ad Hoc ${DateTime.now().toFormat('LLL yyyy')}` : title,
                }),
              }}
              label="* Brand"
              entityKey="brands"
              selectedItemId={owner_id}
            />
          )}

          <Form.Field label="* Title" errorMessage={errors.title} marginTop="large">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          {newRecord && showCreateFromUsageReport && (
            <>
              <SectionDivider boxProps={{ marginY: 'large' }}>OPTIONS</SectionDivider>

              <ToggleListItem
                onClick={() => setState({ createUsingUsageReport: !createUsingUsageReport })}
                toggleValue={createUsingUsageReport}
              >
                <ToggleListItem.Title>Create from Usage Report</ToggleListItem.Title>
                <ToggleListItem.Description>
                  A new project will created using the template usage report
                </ToggleListItem.Description>
              </ToggleListItem>
            </>
          )}

          {createUsingUsageReport && (
            <>
              <Form.Field label="Start Date" marginTop="large">
                <input name="start_date" type="date" value={start_date} {...handlers} />
              </Form.Field>

              <Form.Field label="End Date" marginTop="large">
                <input
                  disabled={!start_date}
                  name="end_date"
                  type="date"
                  value={end_date}
                  min={start_date}
                  {...handlers}
                />
              </Form.Field>
            </>
          )}

          <Form.Field label="* Debtor Type" marginTop="large">
            <select
              name="debtor_type"
              value={debtor_type}
              onChange={e => setEntityState({ debtor_id: undefined, debtor_type: e.target.value })}
            >
              <option value="">Please Select...</option>
              <option value="Agency">Agency</option>
              <option value="Provider">Provider</option>
            </select>
          </Form.Field>

          {!!debtor_type && (
            <OrganizationSelector
              callbacks={{
                // @ts-expect-error TS2339
                selectItem: ({ entity }) => setEntityState({ debtor_id: entity?.id }),
              }}
              entityType={debtor_type}
              label="* Debtor"
              marginTop="large"
              selectedItemId={debtor_id}
            />
          )}

          <EntitySelector
            // @ts-expect-error TS2741
            callbacks={{
              selectItem: taskListTemplate => setEntityState({
                checklist_template_id: taskListTemplate?.id,
              }),
            }}
            label="Checklist Template"
            entityKey="taskListTemplates"
            marginTop="large"
            searchRequestOptions={{
              include_global_task_list_templates: true,
              owner_type,
              owner_id,
              template_checklist: true,
            }}
            selectedItemId={checklist_template_id}
          />

          <Form.Field label="Due Date" marginTop="large">
            <input name="due_date" type="date" value={due_date} {...handlers} />
          </Form.Field>

          {!ad_hoc && (
            <>
              <Form.Field label="Notifications" marginTop="large">
                {/*
                 // @ts-expect-error TS2322 */}
                <select name="notification_frequency" value={notification_frequency} {...handlers}>
                  <option value="">Please Select...</option>
                  <option value="never">Never</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </Form.Field>

              {(notification_frequency === 'daily' || notification_frequency === 'weekly') && (
                <>
                  <EntitySelector
                    // @ts-expect-error TS2741
                    callbacks={{
                      getSearchEntities: entities => entities.map((entity) => {
                        const { id, full_name } = entity
                        return { ...entity, full_name: `#${id} - ${full_name}` }
                      }),
                      selectItem: user => setEntityState({
                        // @ts-expect-error TS18047
                        notification_emails: toggleArray(notification_emails, user.email),
                      }),
                    }}
                    label="Notification Recipients"
                    entityKey="users"
                    marginTop="large"
                    showEntityId
                    showSelectedEntity={false}
                  />

                  {notification_emails.map((email: string) => (
                    <SidebarModalListItem boxProps={{ paddingY: 'medium' }} key={email} title={email}>
                      {/*
                       // @ts-expect-error TS2339 */}
                      <SidebarModalListItem.Actions>
                        {/*
                         // @ts-expect-error TS2339 */}
                        <SidebarModalListItem.Icon
                          marginRight="medium"
                          onClick={() => setEntityState({
                            notification_emails: toggleArray(notification_emails, email),
                          })}
                          type="delete"
                        />
                      {/*
                       // @ts-expect-error TS2339 */}
                      </SidebarModalListItem.Actions>
                    </SidebarModalListItem>
                  ))}
                </>
              )}
            </>
          )}

          {!newRecord && (
            <Form.Field label="More Options" marginTop="large">
              {toggleTaskListCompletion && (
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={completed ? faTimes : faCheck} />}
                  onClick={() => confirmCompletion({ callbacks, entityState, toggleCompletionFn })}
                  // @ts-expect-error
                  marginBottom="small"
                  size="medium"
                >
                  {completed ? 'Mark as Incomplete' : 'Complete Project'}
                </Button>
              )}
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              // @ts-expect-error TS2345
              ? () => createTaskListFn(createOrUpdateTaskListPayload)
              // @ts-expect-error TS2345
              : () => updateTaskList(createOrUpdateTaskListPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? createButtonText : 'Update Project'}` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditTaskListModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
